import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Form,
  Button,
  Alert,
  Image,
  Row,
  Col,
  Accordion,
  Tabs,
  Tab,
  Table,
  Badge,
} from "react-bootstrap";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import api from "../api"; // Adjust path if needed (e.g., '../../api')
import EbayItemSpecifics from "../components/EbayItemSpecifics";
import AsyncSelect from "react-select/async";

// Normalize the multiple item-specific formats returned by the database and AI.
function parseEbaySpecifics(value) {
  if (!value) return {};
  let parsed = value;
  if (typeof parsed === "string") {
    try { parsed = JSON.parse(parsed); } catch (_) { return {}; }
  }
  if (Array.isArray(parsed)) {
    return Object.fromEntries(parsed.filter((item) => item && item.Name).map((item) => [
      String(item.Name), Array.isArray(item.Value) ? item.Value.join(", ") : String(item.Value ?? "")
    ]));
  }
  if (parsed && typeof parsed === "object") {
    return Object.fromEntries(Object.entries(parsed).map(([key, val]) => [
      key, Array.isArray(val) ? val.join(", ") : String(val ?? "")
    ]));
  }
  return {};
}

function looksLikeSingleCard(product) {
  const text = [product.name, product.category, product.ebay_category_label, product.ebay_category_path]
    .filter(Boolean).join(" ").toLowerCase();
  if (/\b(booster|etb|elite trainer|sealed|tin|bundle|box|blister|pack)\b/.test(text)) return false;
  return ["183050", "183454", "261328"].includes(String(product.ebay_category_id || "")) ||
    /\b(trading card single|individual card|pokemon card|pokémon card|tcg single)\b/.test(text);
}

const ItemType = "IMAGE";

function normalizeProductBins(productData) {
  const possibleBins =
    productData?.bins ||
    productData?.inventory_bins ||
    productData?.inventoryBins ||
    [];

  if (!Array.isArray(possibleBins)) return [];

  return possibleBins
    .map((bin) => ({
      id: bin.id || bin.bin_id || bin.binId || bin.inventory_id || `${bin.location}-${bin.quantity}`,
      bin_id: bin.bin_id || bin.binId || bin.id || "",
      location:
        bin.location ||
        bin.bin_location ||
        bin.binLocation ||
        bin.barcode ||
        bin.name ||
        "",
      barcode: bin.barcode || "",
      quantity: Number(bin.quantity || bin.qty || bin.stock || 0),
    }))
    .filter((bin) => bin.location || bin.bin_id || bin.quantity > 0);
}

function getTotalBinQuantity(bins) {
  if (!Array.isArray(bins)) return 0;
  return bins.reduce((sum, bin) => sum + Number(bin.quantity || 0), 0);
}

const DraggableImage = ({ url, index, moveImage, platform }) => {
  const [, ref] = useDrag({
    type: `${ItemType}-${platform}`,
    item: { index, platform },
  });

  const [, drop] = useDrop({
    accept: `${ItemType}-${platform}`,
    hover: (item) => {
      if (item.platform !== platform) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      moveImage(dragIndex, hoverIndex, platform);
      item.index = hoverIndex;
    },
  });

  return (
    <div ref={(node) => ref(drop(node))} style={{ margin: "0 5px", cursor: "grab" }}>
      <Image
        src={url}
        alt={`${platform} Img ${index + 1}`}
        thumbnail
        style={{ maxWidth: "100px" }}
      />
    </div>
  );
};

function Editor() {
  const { sku } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prepareEbayWithAI = location.state?.prepareEbayWithAI === true;
  const fifoOrderFromUrl = new URLSearchParams(location.search).get("fifo_order");
  const fifoTargetRef = useRef(null);
  const hasScrolledToFifoRef = useRef(false);

  const [product, setProduct] = useState({
    sku: "",
    name: "",
    description: "",
    price: "0",
    stock: "0",
    fifo_batches: [],
    image: "",
    listedOn: [],
    upc: "",
    category: "",
    brand: "",
    condition: "",
    manufacturer: "",
    model_number: "",
    msrp: "",
    package_weight: "",
    package_dimensions: { length: "", width: "", height: "" },
    store_product_url: "",
    notes: "",
    shopify_id: "",
    ebay_item_id: "",
    ebay_offer_id: "",
    ebay_draft_status: "",
    ebay_draft_error: "",
    walmart_item_id: "",
    ebay_title: "",
    shopify_title: "",
    walmart_title: "",
    ebay_price: "0",
    shopify_price: "0",
    walmart_price: "0",
    cost_price: "0",
    zero_quantity_action: "keep",
    restock_enabled: 0,
    restock_threshold: 5,

    // ✅ Multi-bin support
    bin_location: "",
    bins: [],
    inventory_bins: [],
    inventoryBins: [],
    bin_count: 0,
    total_bin_quantity: 0,
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
const [aiListingPreview, setAiListingPreview] = useState(null);
  const [aiPreviewDirty, setAiPreviewDirty] = useState(false);
  const [aiPreviewSaving, setAiPreviewSaving] = useState(false);
  const [images, setImages] = useState({
    shopify: [],
    ebay: [],
    walmart: [],
  });
  const [editingEbayHtml, setEditingEbayHtml] = useState(false);
  const [activeAccordionKey, setActiveAccordionKey] = useState(
  prepareEbayWithAI ? "5" : fifoOrderFromUrl ? "2" : "0"
);
  const originalStockRef = useRef(null);

  const fetchShopifyData = useCallback(
    async (shopifyId) => {
      try {
        const response = await api.get(`/shopify/product/${shopifyId}`);
        const shopifyProduct = response.data;
        console.log("Fetched Shopify product data:", shopifyProduct);

        setProduct((prev) => ({
          ...prev,
          upc: shopifyProduct.upc || prev.upc,
          category: shopifyProduct.category || prev.category,
          brand: shopifyProduct.brand || prev.brand,
          description: shopifyProduct.description || prev.description,
          shopify_title: shopifyProduct.title || prev.shopify_title,
          shopify_price: shopifyProduct.price || prev.shopify_price || "0",

          // Do NOT overwrite local dashboard stock from Shopify here.
          // Shopify can lag behind after a save and make the screen look stale.
          stock: prev.stock || "0",

          model_number: shopifyProduct.modelNumber || prev.model_number,
          cost_price: shopifyProduct.cost_price || prev.cost_price || "0",
          zero_quantity_action:
            shopifyProduct.zero_quantity_action || prev.zero_quantity_action || "keep",
        }));

        setImages((prev) => ({
          ...prev,
          shopify: shopifyProduct.images?.map((img) => img.src) || prev.shopify,
        }));
      } catch (err) {
        console.error("Error fetching Shopify data:", err);
        if (err.response?.status === 401) {
          setError("Session expired. Please log in again.");
          localStorage.removeItem("token");
          setTimeout(() => navigate("/login"), 1500);
        } else {
          setError(
            "Failed to load Shopify data: " +
              (err.response?.data?.error || err.message)
          );
        }
      }
    },
    [navigate]
  );

  const fetchProduct = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Not authenticated. Please log in.");
      navigate("/login");
      return;
    }

    try {
      const response = await api.get(`/products/${sku}`);
      const productData = response.data;
      const normalizedBins = normalizeProductBins(productData);
      const totalBinQuantity = getTotalBinQuantity(normalizedBins);

      console.log("Fetched product data:", productData);
      console.log("Normalized product bins:", normalizedBins);

      originalStockRef.current = Number(productData.stock || 0);

      setProduct({
        ...productData,
        bin_location: productData.bin_location || "",
        bins: normalizedBins,
        inventory_bins: normalizedBins,
        inventoryBins: normalizedBins,
        bin_count: normalizedBins.length,
        total_bin_quantity: totalBinQuantity,
        price: productData.price || "0",
        stock: productData.stock || "0",
        condition: productData.condition || "",
        fifo_batches: productData.fifo_batches || [],
        manufacturer: productData.manufacturer || "",
        msrp: productData.msrp || "",
        package_weight: productData.package_weight || "",
        store_product_url: productData.store_product_url || "",
        notes: productData.notes || "",
        package_dimensions: productData.package_dimensions || {
          length: "",
          width: "",
          height: "",
        },
        listedOn: productData.platform ? productData.platform.split(",") : [],
        ebay_title: productData.ebay_title || productData.name || "",
        ebay_offer_id: productData.ebay_offer_id || "",
        ebay_draft_status: productData.ebay_draft_status || "",
        ebay_draft_error: productData.ebay_draft_error || "",
        shopify_title: productData.name || "",
        walmart_title: productData.name || "",
        ebay_price: productData.price || "0",
        shopify_price: productData.price || "0",
        walmart_price: productData.price || "0",
        cost_price: productData.cost_price || "0",
        zero_quantity_action: productData.zero_quantity_action || "keep",
        restock_enabled: productData.restock_enabled ?? 0,
        restock_threshold: productData.restock_threshold ?? 5,
      });

      setImages({
        shopify: productData.shopify_images || [],
        ebay: productData.ebay_images || [],
        walmart: productData.walmart_images || [],
      });

      if (productData.shopify_id) fetchShopifyData(productData.shopify_id);
    } catch (err) {
      console.error("Error fetching product:", err);
      if (err.response?.status === 401) {
        setError("Session expired. Please log in again.");
        localStorage.removeItem("token");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        setError(
          "Failed to load product data: " +
            (err.response?.data?.error || err.message)
        );
      }
    }
  }, [sku, navigate, fetchShopifyData]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  useEffect(() => {
    if (!fifoOrderFromUrl) return;
    if (hasScrolledToFifoRef.current) return;
    if (!product.fifo_batches || product.fifo_batches.length === 0) return;

    const matchingBatch = product.fifo_batches.find((batch) =>
      String(batch.notes || "").includes(fifoOrderFromUrl)
    );

    if (!matchingBatch) return;

    const timer = setTimeout(() => {
      if (fifoTargetRef.current) {
        fifoTargetRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        hasScrolledToFifoRef.current = true;
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [fifoOrderFromUrl, product.fifo_batches]);

  const handleChange = (field) => (e) => {
    const value = e.target.value === "" ? null : e.target.value;
    if (field === "price" || field === "stock" || field.includes("_price")) {
      setProduct({ ...product, [field]: value || "0" });
    } else {
      setProduct({ ...product, [field]: value });
    }
  };

  const handleDimensionChange = (dimension) => (e) => {
    const value = e.target.value === "" ? null : e.target.value;
    setProduct({
      ...product,
      package_dimensions: { ...product.package_dimensions, [dimension]: value },
    });
  };

  const handleFileChange = (e) => setSelectedImage(e.target.files[0]);

  const moveImage = (fromIndex, toIndex, platform) => {
    setImages((prevImages) => {
      const newImages = [...prevImages[platform]];
      const [movedItem] = newImages.splice(fromIndex, 1);
      newImages.splice(toIndex, 0, movedItem);
      console.log(`Reordered ${platform} images:`, newImages);
      return { ...prevImages, [platform]: newImages };
    });
  };

  const loadEbayCategories = async (inputValue) => {
  const search = String(inputValue || "").trim();
  if (search.length < 2) return [];

  try {
    const response = await api.get("/ebay/categories", {
      params: { search },
    });

    return (response.data?.categories || []).map((cat) => {
      const path =
        cat.categoryPath || cat.fullPath || cat.categoryName || "";

      return {
        value: String(cat.categoryId),
        label: `${path} (ID: ${cat.categoryId})`,
        rawName: cat.categoryName || "",
        path,
      };
    });
  } catch (err) {
    console.error("eBay category search failed:", err);
    setError("Unable to search eBay categories.");
    return [];
  }
};

const generateEbayAIPreview = async () => {
  if (aiGenerating) return;

  setAiGenerating(true);
  setError("");
  setAiListingPreview(null);
  setAiPreviewDirty(false);

  try {
    const parseImages = (value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [value];
        } catch {
          return value ? [value] : [];
        }
      }
      return [];
    };

    const savedImages = [
      ...parseImages(images.ebay),
      ...parseImages(images.shopify),
      product.image,
    ]
      .filter(Boolean)
      .map((item) => typeof item === "string" ? item : item?.src || item?.url)
      .filter(Boolean);

    const uniqueImages = [...new Set(savedImages)].slice(0, 12);

    if (!uniqueImages.length) {
      throw new Error("This product has no photos available for AI.");
    }

    const formData = new FormData();
    let attached = 0;

    for (const imageUrl of uniqueImages) {
      try {
        const url = new URL(imageUrl, window.location.origin);
        if (!["http:", "https:"].includes(url.protocol)) continue;

        const response = await fetch(url.toString(), {
          credentials: url.origin === window.location.origin ? "same-origin" : "omit",
        });

        if (!response.ok) continue;

        const blob = await response.blob();
        if (!blob.type.startsWith("image/")) continue;

        formData.append(
          "images",
          blob,
          `inventory-${attached + 1}.${blob.type === "image/png" ? "png" : "jpg"}`
        );
        attached += 1;
      } catch (imageError) {
        console.warn("Could not load AI image:", imageError);
      }
    }

    if (!attached) {
      throw new Error(
        "Could not access saved photos. Image URLs may require server-side loading."
      );
    }

    const specifics = Array.isArray(product.ebay_specifics)
      ? Object.fromEntries(
          product.ebay_specifics
            .filter((s) => s?.Name)
            .map((s) => [s.Name, s.Value])
        )
      : typeof product.ebay_specifics === "string"
        ? JSON.parse(product.ebay_specifics || "{}")
        : product.ebay_specifics || {};

    formData.append("sku", product.sku);
    formData.append("currentTitle", product.name || "");
    formData.append("currentDescription", product.description || "");
    formData.append("currentPrice", String(product.price || ""));
    formData.append("currentCategoryId", String(product.ebay_category_id || ""));
    formData.append(
      "currentCategoryLabel",
      product.ebay_category_path || product.ebay_category_label || ""
    );
    formData.append("currentSpecifics", JSON.stringify(specifics));
    formData.append("conditionHint", product.condition || "");
    formData.append("categoryLocked", product.ebay_category_id ? "true" : "false");

    const response = await api.post(
      "/ai/ebay-listing/generate",
      formData,
      { timeout: 240000 }
    );

    if (!response.data?.success || !response.data?.listing) {
      throw new Error("AI did not return a listing.");
    }

    setAiListingPreview(response.data.listing);
    setAiPreviewDirty(true);
    setActiveAccordionKey("5");
  } catch (err) {
    setError(
      err.response?.data?.error ||
      err.message ||
      "AI listing generation failed."
    );
  } finally {
    setAiGenerating(false);
  }
};

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const priceNum = Number(product.price);
    const stockNum = Number(product.stock);
    const costNum = Number(product.cost_price);

    if (!product.name) return setError("Name is required.");
    if (!product.price || isNaN(priceNum) || priceNum <= 0) {
      return setError("Price is required and must be a positive number.");
    }
    if (product.stock === "" || isNaN(stockNum) || stockNum < 0) {
      return setError("Stock is required and must be a non-negative number.");
    }
    if (isNaN(costNum) || costNum < 0) {
      return setError("Cost price must be a non-negative number.");
    }

    const payload = {
      name: product.name,
      description: product.description,
      ebay_description_html: product.ebay_description_html,
      price: product.price,
      stock: product.stock,
      upc: product.upc,
      category: product.category,
      brand: product.brand,
      condition: product.condition,
      manufacturer: product.manufacturer,
      model_number: product.model_number,
      msrp: product.msrp,
      package_weight: product.package_weight,
      package_dimensions: product.package_dimensions,
      store_product_url: product.store_product_url,
      notes: product.notes,
      shopify_images: images.shopify.length > 0 ? images.shopify : null,
      ebay_images: images.ebay.length > 0 ? images.ebay : null,
      walmart_images: images.walmart.length > 0 ? images.walmart : null,
      platform: product.listedOn.length > 0 ? product.listedOn.join(",") : null,
      cost_price: product.cost_price,
      zero_quantity_action: product.zero_quantity_action,
      ebay_specifics: product.ebay_specifics,
      ebay_category_id: product.ebay_category_id,
      ebay_category_name: product.ebay_category_name,
      ebay_category_label: product.ebay_category_label,
      ebay_category_path: product.ebay_category_path,

      // ✅ Keep old single-bin field for backward compatibility.
      // Multi-bin quantities should be changed through your assign/move bin routes,
      // not by editing this one field.
      bin_location: product.bin_location,

      restock_enabled: product.restock_enabled,
      restock_threshold: product.restock_threshold,
    };

    // 🧹 Clean numeric fields — ALLOW ZERO VALUES
    ["price", "stock", "cost_price", "msrp", "package_weight"].forEach((field) => {
      if (!(field in payload)) return;

      const num = Number(payload[field]);

      if (Number.isNaN(num)) {
        delete payload[field];
      } else {
        payload[field] = num;
      }
    });

    console.log("Payload before sending:", payload);

    const originalStock = Number(originalStockRef.current || 0);
    const requestedStock = Number(payload.stock || 0);
    const stockChanged = originalStock !== requestedStock;

    setSaving(true);

    try {
      let response;

      const hasFifoBatches =
        Array.isArray(product.fifo_batches) && product.fifo_batches.length > 0;

      if (stockChanged && hasFifoBatches) {
        await api.post(`/products/fifo/${product.sku}/adjust-stock`, {
          oldQty: originalStock,
          newQty: requestedStock,
        });
      }

      if (stockChanged) {
        payload.stock = requestedStock;
      }

      if (selectedImage) {
        const formData = new FormData();
        formData.append("image", selectedImage);

        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            formData.append(
              key,
              key.includes("images") || key === "package_dimensions"
                ? JSON.stringify(value)
                : value
            );
          }
        });

        console.log("FormData entries:", [...formData.entries()]);
        response = await api.put(`/products/${sku}`, formData);
      } else {
        response = await api.put(`/products/${sku}`, payload);
      }

      try {
        if (product.listedOn.includes("Shopify")) {
          console.log("[Editor] Triggering Shopify stock sync...");
          const shopifyRes = await api.post("/shopify/sync-quantity", {
            sku: product.sku,
            quantity: requestedStock,
          });
          console.log("[Editor] Shopify stock sync result:", shopifyRes.data);
        }

        if (product.listedOn.includes("eBay")) {
          console.log("[Editor] Triggering eBay stock sync...");
          const ebayRes = await api.post("/ebay/sync-quantity", {
            sku: product.sku,
            quantity: requestedStock,
          });
          console.log("[Editor] eBay stock sync result:", ebayRes.data);
        }

        if (product.listedOn.includes("Walmart")) {
          console.log("[Editor] Triggering Walmart stock sync...");
          const walmartRes = await api.post("/walmart/sync-quantity", {
            sku: product.sku,
            quantity: requestedStock,
          });
          console.log("[Editor] Walmart stock sync result:", walmartRes.data);
        }
      } catch (syncErr) {
        console.error(
          "[Editor] Platform sync failed:",
          syncErr.response?.data || syncErr.message
        );
      }

      const data = response.data;
      let successMessage = "Product updated successfully!";

      if (data.warnings) {
        successMessage +=
          " Some platforms failed to sync: " + data.warnings.join(", ");
      } else if (data.syncResults) {
        const syncedPlatforms = [];
        if (data.syncResults.eBay?.synced) syncedPlatforms.push("eBay");
        if (data.syncResults.Shopify?.synced) syncedPlatforms.push("Shopify");
        if (data.syncResults.Walmart?.synced) syncedPlatforms.push("Walmart");

        if (syncedPlatforms.length > 0) {
          successMessage = `Product synced to ${syncedPlatforms.join(
            ", "
          )} successfully!`;
        }
      }

      setSuccess(successMessage);

      setProduct((prev) => ({
        ...prev,
        stock: String(requestedStock),
      }));

      originalStockRef.current = requestedStock;

      await fetchProduct();
    } catch (err) {
      console.error("Error updating product:", err);
      if (err.response?.status === 401) {
        setError("Session expired. Please log in again.");
        localStorage.removeItem("token");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        setError(
          "Failed to update product: " +
            (err.response?.data?.error || err.message)
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleListOnPlatform = (platform) => {
    setProduct((prev) => ({
      ...prev,
      listedOn: prev.listedOn.includes(platform)
        ? prev.listedOn.filter((p) => p !== platform)
        : [...prev.listedOn, platform],
    }));
  };

  const productBins = Array.isArray(product.bins) ? product.bins : [];
  const totalBinQuantity = getTotalBinQuantity(productBins);

  return (
    <DndProvider backend={HTML5Backend}>
      <Container className="my-4">
        <h2 className="mb-4">Edit Product: {product.sku}</h2>

        {error && <Alert variant="danger">{error}</Alert>}

        {success && (
          <Alert
            variant="success"
            onClose={() => setSuccess("")}
            dismissible
          >
            {success}
          </Alert>
        )}

        <Form onSubmit={handleSave}>
          <Accordion
            activeKey={activeAccordionKey}
            onSelect={(key) => setActiveAccordionKey(key)}
            className="mb-4"
          >
            <Accordion.Item eventKey="0">
              <Accordion.Header>Basic Information</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>SKU</Form.Label>
                      <Form.Control value={product.sku} readOnly />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        value={product.name || ""}
                        onChange={handleChange("name")}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Primary / Legacy Bin Location</Form.Label>
                      <Form.Control
                        value={product.bin_location || ""}
                        onChange={(e) =>
                          setProduct({ ...product, bin_location: e.target.value })
                        }
                        placeholder="Enter bin manually"
                      />
                      <Form.Text className="text-muted">
                        This is the old single-bin field. Multi-bin quantities are shown below.
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <div className="mb-3">
                      <Form.Label>Multi-Bin Summary</Form.Label>
                      <div className="border rounded p-2 bg-light">
                        <div>
                          <strong>Total Bins:</strong>{" "}
                          <Badge bg={productBins.length > 1 ? "primary" : "secondary"}>
                            {productBins.length}
                          </Badge>
                        </div>
                        <div>
                          <strong>Total Bin Quantity:</strong>{" "}
                          <Badge
                            bg={
                              Number(product.stock || 0) === totalBinQuantity
                                ? "success"
                                : "warning"
                            }
                            text={
                              Number(product.stock || 0) === totalBinQuantity
                                ? undefined
                                : "dark"
                            }
                          >
                            {totalBinQuantity}
                          </Badge>
                        </div>
                        <div>
                          <strong>Product Stock:</strong>{" "}
                          <Badge bg="dark">{Number(product.stock || 0)}</Badge>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="mb-0">Bin Locations</Form.Label>
                        {productBins.length > 1 && (
                          <Badge bg="info">Multi-Bin Product</Badge>
                        )}
                      </div>

                      {productBins.length > 0 ? (
                        <Table striped bordered hover responsive size="sm">
                          <thead>
                            <tr>
                              <th>Bin ID</th>
                              <th>Location</th>
                              <th>Barcode</th>
                              <th className="text-end">Quantity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {productBins.map((bin, index) => (
                              <tr key={`${bin.id || bin.bin_id || index}`}>
                                <td>{bin.bin_id || "-"}</td>
                                <td>
                                  <strong>{bin.location || "-"}</strong>
                                </td>
                                <td>{bin.barcode || "-"}</td>
                                <td className="text-end">
                                  <Badge bg="secondary">
                                    {Number(bin.quantity || 0)}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan="3" className="text-end">
                                <strong>Total</strong>
                              </td>
                              <td className="text-end">
                                <strong>{totalBinQuantity}</strong>
                              </td>
                            </tr>
                          </tfoot>
                        </Table>
                      ) : (
                        <Alert variant="secondary" className="mb-0">
                          No bin rows found for this product.
                        </Alert>
                      )}

                      {productBins.length > 0 &&
                        Number(product.stock || 0) !== totalBinQuantity && (
                          <Alert variant="warning" className="mt-2 mb-0">
                            Product stock is {Number(product.stock || 0)}, but bin
                            quantity total is {totalBinQuantity}. This means stock and
                            bin inventory are not matching.
                          </Alert>
                        )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Brand</Form.Label>
                      <Form.Control
                        value={product.brand || ""}
                        onChange={handleChange("brand")}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Category</Form.Label>
                      <Form.Control
                        value={product.category || ""}
                        onChange={handleChange("category")}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  {product.description && /<[^>]+>/.test(product.description) ? (
                    <div
                      className="p-2 border rounded description-preview"
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        whiteSpace: "normal",
                      }}
                      dangerouslySetInnerHTML={{ __html: product.description }}
                    />
                  ) : (
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={product.description || ""}
                      onChange={handleChange("description")}
                    />
                  )}
                </Form.Group>


              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>Pricing & Inventory</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Price ($)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={product.price}
                        onChange={handleChange("price")}
                        required
                        min="0.01"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>MSRP ($)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={product.msrp || ""}
                        onChange={handleChange("msrp")}
                        min="0"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stock</Form.Label>
                      <Form.Control
                        type="number"
                        value={product.stock}
                        onChange={handleChange("stock")}
                        required
                        min="0"
                      />
                      {productBins.length > 0 && (
                        <Form.Text
                          className={
                            Number(product.stock || 0) === totalBinQuantity
                              ? "text-success"
                              : "text-warning"
                          }
                        >
                          Bin total: {totalBinQuantity}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {productBins.length > 0 && (
                  <div className="mb-3">
                    <h6>Inventory by Bin</h6>
                    <Table striped bordered hover responsive size="sm">
                      <thead>
                        <tr>
                          <th>Location</th>
                          <th>Bin ID</th>
                          <th className="text-end">Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productBins.map((bin, index) => (
                          <tr key={`pricing-bin-${bin.id || bin.bin_id || index}`}>
                            <td>{bin.location || "-"}</td>
                            <td>{bin.bin_id || "-"}</td>
                            <td className="text-end">
                              <strong>{Number(bin.quantity || 0)}</strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                <Row className="mt-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Enable Restock Alert"
                        checked={product.restock_enabled === 1}
                        onChange={(e) =>
                          setProduct({
                            ...product,
                            restock_enabled: e.target.checked ? 1 : 0,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Restock Threshold</Form.Label>
                      <Form.Control
                        type="number"
                        value={product.restock_threshold || ""}
                        onChange={(e) =>
                          setProduct({
                            ...product,
                            restock_threshold: e.target.value,
                          })
                        }
                        min="0"
                      />

                      <Button
                        variant="outline-warning"
                        size="sm"
                        className="mt-2"
                        onClick={async () => {
                          try {
                            await api.post("/restock/reset", { sku: product.sku });
                            alert("Alert reset");
                          } catch (err) {
                            alert("Failed to reset alert");
                            console.error(err);
                          }
                        }}
                      >
                        🔄 Reset Alert
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Keep in inventory when stock reaches 0"
                        checked={product.zero_quantity_action === "keep"}
                        onChange={(e) =>
                          setProduct({
                            ...product,
                            zero_quantity_action: e.target.checked
                              ? "keep"
                              : "delete",
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="2">
              <Accordion.Header>Cost</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cost Price ($)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={product.cost_price}
                        onChange={handleChange("cost_price")}
                        min="0"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h5 className="mt-4">FIFO Purchase Batches</h5>

                <div className="mb-3 d-flex gap-2">
                  <Button
                    variant="success"
                    onClick={async () => {
                      const qty = prompt("Enter purchase quantity:");
                      const cost = prompt("Enter purchase cost per item:");
                      const notes = prompt("Notes (optional):") || "";

                      if (!qty || !cost) return alert("Both fields required");

                      try {
                        await api.post(`/products/fifo/${product.sku}/add-batch`, {
                          qty: Number(qty),
                          cost: Number(cost),
                          notes,
                        });

                        alert("Batch added to stock!");
                        fetchProduct();
                      } catch (err) {
                        alert("Failed to add batch: " + err.message);
                      }
                    }}
                  >
                    + Add Batch (In Stock)
                  </Button>

                  <Button
                    variant="warning"
                    onClick={async () => {
                      const qty = prompt("Enter purchase quantity:");
                      const cost = prompt("Enter purchase cost per item:");
                      const notes = prompt("Notes (optional):") || "";

                      if (!qty || !cost) return alert("Both fields required");

                      try {
                        await api.post(
                          `/products/fifo/${product.sku}/add-pending-batch`,
                          {
                            qty: Number(qty),
                            cost: Number(cost),
                            notes,
                          }
                        );

                        alert("Pending batch created!");
                        fetchProduct();
                      } catch (err) {
                        alert("Failed to add pending batch: " + err.message);
                      }
                    }}
                  >
                    + Add Pending Batch
                  </Button>
                </div>

                <div>
                  <Row className="fw-bold mb-2">
                    <Col>Purchase Date</Col>
                    <Col>Notes</Col>
                    <Col>Qty Bought</Col>
                    <Col>Remaining</Col>
                    <Col>Cost</Col>
                    <Col>Actions</Col>
                  </Row>

                  {product.fifo_batches && product.fifo_batches.length > 0 ? (
                    product.fifo_batches.map((batch) => (
                      <Row
                        key={batch.id}
                        ref={
                          fifoOrderFromUrl &&
                          String(batch.notes || "").includes(fifoOrderFromUrl)
                            ? fifoTargetRef
                            : null
                        }
                        className="mb-2 align-items-center"
                        style={{
                          backgroundColor:
                            fifoOrderFromUrl &&
                            String(batch.notes || "").includes(fifoOrderFromUrl)
                              ? "rgba(255, 193, 7, 0.18)"
                              : undefined,
                          border:
                            fifoOrderFromUrl &&
                            String(batch.notes || "").includes(fifoOrderFromUrl)
                              ? "2px solid #ffca2c"
                              : undefined,
                          borderRadius:
                            fifoOrderFromUrl &&
                            String(batch.notes || "").includes(fifoOrderFromUrl)
                              ? "6px"
                              : undefined,
                          padding:
                            fifoOrderFromUrl &&
                            String(batch.notes || "").includes(fifoOrderFromUrl)
                              ? "6px 0"
                              : undefined,
                        }}
                      >
                        <Col>
                          {new Date(batch.purchase_date).toLocaleDateString()}
                        </Col>

                        <Col>
                          {batch.notes ? (
                            <span title={batch.notes}>
                              {batch.notes.length > 25
                                ? batch.notes.substring(0, 25) + "..."
                                : batch.notes}
                            </span>
                          ) : (
                            "-"
                          )}
                        </Col>

                        <Col>{batch.purchase_qty}</Col>
                        <Col>{batch.remaining_qty}</Col>
                        <Col>${batch.purchase_cost}</Col>

                        <Col>
                          {batch.status === "pending" && (
                            <span className="badge bg-warning text-dark me-2">
                              Pending
                            </span>
                          )}

                          {(batch.status === "released" || !batch.status) && (
                            <span className="badge bg-success me-2">In Stock</span>
                          )}

                          {batch.status === "pending" && (
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="me-2"
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    "Release this batch into inventory?"
                                  )
                                ) {
                                  return;
                                }

                                try {
                                  await api.post(
                                    `/products/fifo/batches/${batch.id}/release`
                                  );
                                  alert("Batch released!");
                                  fetchProduct();
                                } catch (err) {
                                  alert("Release failed: " + err.message);
                                }
                              }}
                            >
                              Release
                            </Button>
                          )}

                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={async () => {
                              const newNotes = prompt(
                                "Edit notes:",
                                batch.notes || ""
                              );

                              if (newNotes === null) return;
                              if (newNotes.trim() === batch.notes) return;

                              try {
                                await api.put(
                                  `/products/fifo/${product.sku}/batches/${batch.id}`,
                                  {
                                    notes: newNotes,
                                  }
                                );

                                alert("Notes updated!");
                                fetchProduct();
                              } catch (err) {
                                alert("Failed to update notes: " + err.message);
                              }
                            }}
                          >
                            Edit Notes
                          </Button>

                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={async () => {
                              const newQty = prompt(
                                "New purchase qty:",
                                batch.purchase_qty
                              );
                              const newCost = prompt(
                                "New cost:",
                                batch.purchase_cost
                              );

                              if (newQty === null || newCost === null) return;

                              if (
                                isNaN(Number(newQty)) ||
                                isNaN(Number(newCost))
                              ) {
                                return alert("Invalid number");
                              }

                              try {
                                await api.put(
                                  `/products/fifo/${product.sku}/batches/${batch.id}`,
                                  {
                                    purchase_qty: Number(newQty),
                                    purchase_cost: Number(newCost),
                                  }
                                );

                                alert("Batch updated!");
                                fetchProduct();
                              } catch (err) {
                                alert(
                                  "Update failed: " +
                                    (err.response?.data?.error || err.message)
                                );
                              }
                            }}
                          >
                            Edit
                          </Button>

                          <Button
                            variant="outline-dark"
                            size="sm"
                            className="me-2"
                            onClick={async () => {
                              const newRemaining = prompt(
                                "New remaining qty:",
                                batch.remaining_qty
                              );

                              if (newRemaining === null) return;

                              if (isNaN(Number(newRemaining))) {
                                return alert("Invalid number");
                              }

                              try {
                                await api.put(
                                  `/products/fifo/${product.sku}/batches/${batch.id}`,
                                  {
                                    remaining_qty: Number(newRemaining),
                                  }
                                );

                                alert("Remaining qty updated!");
                                fetchProduct();
                              } catch (err) {
                                alert(
                                  "Update failed: " +
                                    (err.response?.data?.error || err.message)
                                );
                              }
                            }}
                          >
                            Adjust Qty
                          </Button>

                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={async () => {
                              if (!window.confirm("Delete this batch?")) return;

                              try {
                                await api.delete(
                                  `/products/fifo/${product.sku}/batches/${batch.id}`
                                );
                                alert("Batch deleted");
                                fetchProduct();
                              } catch (err) {
                                alert("Delete failed: " + err.message);
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Col>
                      </Row>
                    ))
                  ) : (
                    <p>No batches found.</p>
                  )}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="3">
              <Accordion.Header>Images</Accordion.Header>
              <Accordion.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Primary Image</Form.Label>
                  {product.image ? (
                    <Image
                      src={
                        product.image.startsWith("http")
                          ? product.image
                          : `/Uploads${product.image}`
                      }
                      alt="Primary Product"
                      thumbnail
                      style={{ maxWidth: "150px" }}
                    />
                  ) : (
                    <p>No primary image available</p>
                  )}
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*"
                    className="mt-2"
                  />
                </Form.Group>

                <Tabs defaultActiveKey="shopify" className="mb-3">
                  <Tab eventKey="shopify" title="Shopify Images">
                    {images.shopify.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {images.shopify.map((url, index) => (
                          <DraggableImage
                            key={url}
                            url={url}
                            index={index}
                            moveImage={moveImage}
                            platform="shopify"
                          />
                        ))}
                      </div>
                    ) : (
                      <p>No Shopify images available</p>
                    )}
                  </Tab>

                  <Tab eventKey="ebay" title="eBay Images">
                    {images.ebay.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {images.ebay.map((url, index) => (
                          <DraggableImage
                            key={url}
                            url={url}
                            index={index}
                            moveImage={moveImage}
                            platform="ebay"
                          />
                        ))}
                      </div>
                    ) : (
                      <p>No eBay images available</p>
                    )}
                  </Tab>

                  <Tab eventKey="walmart" title="Walmart Images">
                    {images.walmart.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {images.walmart.map((url, index) => (
                          <DraggableImage
                            key={url}
                            url={url}
                            index={index}
                            moveImage={moveImage}
                            platform="walmart"
                          />
                        ))}
                      </div>
                    ) : (
                      <p>No Walmart images available</p>
                    )}
                  </Tab>
                </Tabs>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="4">
              <Accordion.Header>Additional Details</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>UPC</Form.Label>
                      <Form.Control
                        value={product.upc || ""}
                        onChange={handleChange("upc")}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Condition</Form.Label>
                      <Form.Select
                        value={product.condition || ""}
                        onChange={handleChange("condition")}
                      >
                        <option value="">Select Condition</option>
                        <option value="New">New</option>
                        <option value="Used">Used</option>
                        <option value="Refurbished">Refurbished</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Manufacturer</Form.Label>
                      <Form.Control
                        value={product.manufacturer || ""}
                        onChange={handleChange("manufacturer")}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Model Number</Form.Label>
                      <Form.Control
                        value={product.model_number || ""}
                        onChange={handleChange("model_number")}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Package Weight (lbs)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={product.package_weight || ""}
                    onChange={handleChange("package_weight")}
                    min="0"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Package Dimensions (in)</Form.Label>
                  <Row>
                    <Col>
                      <Form.Control
                        type="number"
                        step="0.01"
                        placeholder="Length"
                        value={product.package_dimensions.length || ""}
                        onChange={handleDimensionChange("length")}
                        min="0"
                      />
                    </Col>

                    <Col>
                      <Form.Control
                        type="number"
                        step="0.01"
                        placeholder="Width"
                        value={product.package_dimensions.width || ""}
                        onChange={handleDimensionChange("width")}
                        min="0"
                      />
                    </Col>

                    <Col>
                      <Form.Control
                        type="number"
                        step="0.01"
                        placeholder="Height"
                        value={product.package_dimensions.height || ""}
                        onChange={handleDimensionChange("height")}
                        min="0"
                      />
                    </Col>
                  </Row>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Store URL</Form.Label>
                  <Form.Control
                    value={product.store_product_url || ""}
                    onChange={handleChange("store_product_url")}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={product.notes || ""}
                    onChange={handleChange("notes")}
                  />
                </Form.Group>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="5">
              <Accordion.Header>Marketplace Listings &amp; eBay Workflow</Accordion.Header>
              <Accordion.Body>
                <h5>Marketplace Connections</h5>
                <p className="text-muted small">These buttons control platform flags when you save the product. They do not create or publish listings.</p>

                <div className="d-flex gap-2 mb-3">
                  {["Shopify", "eBay", "Walmart"].map((platform) => (
                    <Button
                      key={platform}
                      type="button"
                      variant={
                        product.listedOn.includes(platform)
                          ? "success"
                          : "outline-success"
                      }
                      onClick={() => handleListOnPlatform(platform)}
                    >
                      {platform}
                    </Button>
                  ))}
                </div>

                <Tabs
  defaultActiveKey={prepareEbayWithAI ? "ebay" : "shopify"}
  className="mb-3"
>
                  <Tab eventKey="shopify" title="Shopify">
                    <Form.Group className="mb-3">
                      <Form.Label>Shopify ID</Form.Label>
                      <Form.Control
                        value={product.shopify_id || "Not listed"}
                        readOnly
                      />
                    </Form.Group>
                  </Tab>

                  <Tab eventKey="ebay" title="eBay Workflow">
                    <div className="border rounded-3 p-3 mb-4 bg-light">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                        <h5 className="mb-0">eBay Listing Workspace</h5>
                        <Badge bg={product.ebay_draft_status === "EBAY_PUBLISHED" ? "success" : product.ebay_offer_id ? "info" : "secondary"}>
                          {product.ebay_draft_status === "EBAY_PUBLISHED" ? "Live" : product.ebay_offer_id ? "Draft exists" : "Not sent to eBay"}
                        </Badge>
                      </div>
                      <p className="mb-2 text-muted">Work from top to bottom: generate or edit details, save them locally, create an unpublished eBay draft, then publish only when ready.</p>
                      <div className="d-flex flex-wrap gap-2 small">
                        <span className="px-2 py-1 bg-white border rounded">1. Prepare listing</span>
                        <span className="px-2 py-1 bg-white border rounded">2. Save details</span>
                        <span className="px-2 py-1 bg-white border rounded">3. Create eBay draft</span>
                        <span className="px-2 py-1 bg-white border rounded">4. Publish</span>
                      </div>
                    </div>
                    <h5 className="mb-2">Step 1 — Prepare and review listing details</h5>
                    <Alert variant="secondary" className="small">
                      AI suggestions are not verified facts. Check card set, number, condition, category and required specifics against the actual product.
                      The eBay backend validates category rules when preparing the draft.
                    </Alert>
                    <p className="text-muted small">AI generates a reviewable suggestion; it does not create or publish an eBay listing.</p>
                    <Form.Group className="mb-3">
  <Form.Label>eBay Category</Form.Label>

  <AsyncSelect
    cacheOptions
    loadOptions={loadEbayCategories}
    placeholder="Search Pokémon TCG categories..."
    isClearable={false}
    value={
      product.ebay_category_id
        ? {
            value: String(product.ebay_category_id),
            label: `${
              product.ebay_category_path ||
              product.ebay_category_label ||
              product.ebay_category_name ||
              "eBay Category"
            } (ID: ${product.ebay_category_id})`,
          }
        : null
    }
    onChange={(selected) => {
      if (!selected) return;

      setProduct((prev) => ({
        ...prev,
        ebay_category_id: selected.value,
        ebay_category_name: selected.rawName,
        ebay_category_label: selected.rawName,
        ebay_category_path: selected.path,
      }));
    }}
  />

  <Form.Text className="text-muted">
    Select the category and save. This does not publish the listing.
  </Form.Text>
</Form.Group>

                    <div className="mb-3">
  <Button
    type="button"
    variant="info"
    onClick={generateEbayAIPreview}
    disabled={aiGenerating || !product.sku}
  >
    {aiGenerating ? "Generating AI Listing..." : "Generate AI eBay Listing"}
  </Button>

  {aiListingPreview && (
    <div className="border rounded p-3 mt-3">
      <h5>AI eBay Listing Preview</h5>

      <Form.Label>Suggested Title</Form.Label>
      <Form.Control
        className="mb-3"
        value={aiListingPreview.title || ""}
        onChange={(e) => {
          setAiListingPreview((prev) => ({ ...prev, title: e.target.value }));
          setAiPreviewDirty(true);
        }}
      />

      <Form.Label>Suggested Description</Form.Label>
      <Form.Control
        as="textarea"
        rows={5}
        className="mb-3"
        value={aiListingPreview.description || ""}
        onChange={(e) => {
          setAiListingPreview((prev) => ({ ...prev, description: e.target.value }));
          setAiPreviewDirty(true);
        }}
      />

      <p><strong>Category:</strong> {aiListingPreview.ebay_category_label || aiListingPreview.ebay_category_id || "Needs review"}</p>
      <p><strong>Condition:</strong> {aiListingPreview.condition || "Needs review"}</p>
      <p><strong>Suggested Price:</strong> {aiListingPreview.suggested_price ?? "Not available"}</p>

      <h6>AI Item Specifics — editable</h6>
      <p className="small text-muted">Correct the suggestions below before applying. Blank values are not automatically invented.</p>
      <Row className="mb-2">
        {Object.entries(parseEbaySpecifics(aiListingPreview.item_specifics)).map(([name, value]) => (
          <Col md={6} key={name} className="mb-2">
            <Form.Label className="small">{name}</Form.Label>
            <Form.Control
              value={value}
              onChange={(event) => {
                const updated = { ...parseEbaySpecifics(aiListingPreview.item_specifics), [name]: event.target.value };
                setAiListingPreview((prev) => ({ ...prev, item_specifics: updated }));
                setAiPreviewDirty(true);
              }}
            />
          </Col>
        ))}
      </Row>
      <Form.Group className="mb-3">
        <Form.Label>Add an item specific</Form.Label>
        <div className="d-flex gap-2">
          <Form.Control id="new-ebay-specific-name" placeholder="Field name, e.g. Set" />
          <Button type="button" variant="outline-primary" onClick={() => {
            const element = document.getElementById("new-ebay-specific-name");
            const name = element?.value?.trim();
            if (!name) return;
            setAiListingPreview((prev) => ({ ...prev, item_specifics: { ...parseEbaySpecifics(prev.item_specifics), [name]: "" } }));
            setAiPreviewDirty(true);
            element.value = "";
          }}>Add</Button>
        </div>
      </Form.Group>

            {(aiListingPreview.warnings || []).length > 0 && (
        <Alert variant="warning">
          <strong>Review required:</strong>
          <ul>
            {aiListingPreview.warnings.map((warning, i) => (
              <li key={i}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Button
        type="button"
        variant="success"
        className="mt-3"
        disabled={
          saving || aiPreviewSaving ||
          !aiListingPreview.title?.trim() ||
          !aiListingPreview.description?.trim()
        }
        onClick={async () => {
          try {
            setAiPreviewSaving(true);
            setError("");

            const result = await api.post(
              `/products/${encodeURIComponent(product.sku)}/ebay-ai-draft`,
              {
                title: aiListingPreview.title,
                description: aiListingPreview.description,
                categoryId:
                  product.ebay_category_id ||
                  aiListingPreview.ebay_category_id,
                categoryName: aiListingPreview.category_name,
                categoryLabel: aiListingPreview.ebay_category_label,
                categoryPath: aiListingPreview.category_path,
                itemSpecifics: aiListingPreview.item_specifics || {},
                aiListing: aiListingPreview,
              }
            );

            if (!result.data?.success) throw new Error(result.data?.error || "AI draft was not saved.");
            if (result.data?.success) {
              setAiPreviewDirty(false);
              setSuccess("Reviewed AI eBay listing saved locally. Not published.");
              await fetchProduct();
            }
          } catch (err) {
            setError(
              "Failed to save AI eBay listing: " +
                (err.response?.data?.error || err.message)
            );
          } finally {
            setAiPreviewSaving(false);
          }
        }}
      >
        {aiPreviewSaving ? "Saving reviewed AI..." : "Apply Reviewed AI Details"}
      </Button>
    </div>
  )}
</div>

                    <div className="border rounded p-3 mb-3">
                      <h6>Saved eBay description</h6>
                      <p className="text-muted small">Edit the existing saved description here. Save Changes at the bottom before creating or updating a draft.</p>
                <Form.Group className="mb-3 mt-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <Form.Label>eBay Description</Form.Label>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setEditingEbayHtml(!editingEbayHtml)}
                    >
                      {editingEbayHtml ? "Preview" : "Edit HTML"}
                    </Button>
                  </div>

                  {editingEbayHtml ? (
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      className="p-3 border rounded bg-white"
                      style={{ minHeight: "200px" }}
                      onBlur={(e) => {
                        const html = e.currentTarget?.innerHTML;
                        if (html !== undefined) {
                          setProduct((prev) => ({
                            ...prev,
                            ebay_description_html: html,
                          }));
                        }
                      }}
                      dangerouslySetInnerHTML={{
                        __html: product.ebay_description_html || "<p></p>",
                      }}
                    />
                  ) : (
                    <div
                      className="p-3 border rounded bg-light"
                      style={{ maxHeight: "350px", overflowY: "auto" }}
                      dangerouslySetInnerHTML={{
                        __html:
                          product.ebay_description_html || "<em>No description</em>",
                      }}
                    />
                  )}
                </Form.Group>
                      <h6 className="mt-4">eBay item specifics</h6>
                <EbayItemSpecifics
                  specifics={
                    Array.isArray(product.ebay_specifics)
                      ? product.ebay_specifics
                      : typeof product.ebay_specifics === "string"
                      ? JSON.parse(product.ebay_specifics || "[]")
                      : []
                  }
                  onChange={(name, value) => {
                    const current = Array.isArray(product.ebay_specifics)
                      ? product.ebay_specifics
                      : typeof product.ebay_specifics === "string"
                      ? JSON.parse(product.ebay_specifics || "[]")
                      : [];

                    const updated = current.map((s) =>
                      s.Name === name ? { ...s, Value: value } : s
                    );

                    setProduct({
                      ...product,
                      ebay_specifics: updated,
                    });
                  }}
                />

                    </div>
                    <div className="border rounded p-3 mb-3">
                      <h6>Shipping policy (applied by backend)</h6>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <Badge bg={looksLikeSingleCard(product) ? "success" : "secondary"}>
                          {product.shipping_policy_id || product.fulfillment_policy_id
                            ? `Product override — ${product.shipping_policy_id || product.fulfillment_policy_id}`
                            : looksLikeSingleCard(product) ? "Expected: Thorns Cards — 281319722015" : "Expected: Default Shipping — 133731495015"}
                        </Badge>
                        <span className="small text-muted">An explicitly assigned product shipping policy takes precedence. Verify the policy in the unpublished eBay offer.</span>
                      </div>
                      <Form.Text>This is a preview based on existing product fields. The server makes the final policy choice. No shipping policy is changed by this display.</Form.Text>
                    </div>
                    {aiPreviewDirty && <Alert variant="warning">You have AI preview changes that have not been applied. Apply reviewed AI details before creating an eBay draft.</Alert>}
                    <h5 className="mt-4 mb-2">Step 2 — Save listing details</h5>
                    <p className="small text-muted">Click <strong>Save Changes</strong> at the bottom of the page after manually changing category, description, or specifics. The AI Apply button saves its preview locally using your existing AI draft endpoint.</p>
                    <h5 className="mt-4 mb-2">Step 3 — Create or update unpublished eBay draft</h5>
<div className="mb-3">
  <Button
    type="button"
    variant="primary"
    disabled={saving || aiPreviewSaving || aiPreviewDirty || !product.sku || Boolean(product.ebay_item_id)}
    onClick={async () => {
      if (aiPreviewDirty) {
        setError("Apply the reviewed AI details before creating the eBay draft.");
        return;
      }
      if (
        !window.confirm(
          `Send the saved eBay listing for ${product.sku} to eBay as an unpublished draft?\n\n` +
          `Existing offer: ${product.ebay_offer_id || "None"}\n\n` +
          `This will modify eBay inventory/offer data, but will NOT publish the listing.`
        )
      ) {
        return;
      }

      try {
        setSaving(true);
        setError("");
        setSuccess("");

        const response = await api.post("/ebay/drafts/create", {
          sku: product.sku,
        });

        if (!response.data?.success) {
          throw new Error(
            response.data?.error || "eBay draft update failed."
          );
        }

        setSuccess(
          `eBay draft ${response.data.action || "saved"} successfully. ` +
          `Offer ID: ${response.data.ebay_offer_id}. Not published.`
        );

        await fetchProduct();
      } catch (err) {
        setError(
          "Failed to create/update eBay draft: " +
          (err.response?.data?.error || err.message)
        );
      } finally {
        setSaving(false);
      }
    }}
  >
    {saving ? "Working..." : "Create / Update eBay Draft"}
  </Button>

  <Form.Text className="d-block text-muted mt-1">
    Sends your saved eBay information to an unpublished eBay offer.
    Does not publish the listing.
  </Form.Text>
</div>


                    <div className="border rounded p-3 mb-4">
                      <h5>Step 4 — Publish or update a live eBay listing</h5>
                      <p className="small text-muted">Publishing makes the offer live on eBay. Confirm category, condition, item specifics, USD price and shipping policy on the unpublished eBay offer first. The backend must enforce final validation.</p>
                      <div className="d-flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="warning"
                  onClick={async () => {
                    try {
                      await api.post("/ebay/descriptions/push", {
                        sku: product.sku,
                      });
                      alert("eBay description updated successfully!");
                    } catch (err) {
                      alert(
                        "Failed to push to eBay: " +
                          (err.response?.data?.error || err.message)
                      );
                      console.error(err);
                    }
                  }}
                >
                  Push Description to eBay
                </Button>

                <Button
                  type="button"
                  variant="success"
                  onClick={async () => {
                    if (
                      !window.confirm(
                        "Publish this item live on eBay now? This will make the listing active."
                      )
                    ) {
                      return;
                    }

                    try {
                      setSaving(true);

                      const res = await api.post("/ebay/drafts/publish", {
                        sku: product.sku,
                      });

                      if (res.data?.success) {
                        alert(
                          `Published to eBay successfully! Listing ID: ${
                            res.data.ebay_listing_id ||
                            res.data.ebay_item_id ||
                            "created"
                          }`
                        );

                        await fetchProduct();
                      } else {
                        alert(res.data?.error || "Failed to publish to eBay.");
                      }
                    } catch (err) {
                      alert(
                        "Failed to publish to eBay: " +
                          (err.response?.data?.error || err.message)
                      );
                      console.error(err);
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={
                    saving ||
                    !product.ebay_offer_id ||
                    product.ebay_draft_status === "EBAY_PUBLISHED" ||
                    Boolean(product.ebay_item_id)
                  }
                >
                  {product.ebay_draft_status === "EBAY_PUBLISHED"
                    ? "Published on eBay"
                    : "Publish to eBay"}
                </Button>

                      </div>
                    </div>
                    <div className="border rounded p-3 mb-3">
                      <h6>Listing status and identifiers</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>eBay Item ID</Form.Label>
                      <Form.Control
                        value={product.ebay_item_id || "Not listed"}
                        readOnly
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>eBay Offer ID</Form.Label>
                      <Form.Control
                        value={product.ebay_offer_id || "No offer created"}
                        readOnly
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>eBay Offer Status</Form.Label>
                      <Form.Control
                        value={product.ebay_draft_status || "Not created"}
                        readOnly
                      />
                    </Form.Group>

                    {product.ebay_draft_error && (
                      <Alert variant="warning">
                        <strong>eBay Error:</strong> {product.ebay_draft_error}
                      </Alert>
                    )}
                    </div>
                  </Tab>

                  <Tab eventKey="walmart" title="Walmart">
                    <Form.Group className="mb-3">
                      <Form.Label>Walmart Item ID</Form.Label>
                      <Form.Control
                        value={product.walmart_item_id || "Not listed"}
                        readOnly
                      />
                    </Form.Group>
                  </Tab>
                </Tabs>
              </Accordion.Body>
            </Accordion.Item>


          </Accordion>

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                if (location.state?.from) {
                  navigate(location.state.from, {
                    state: {
                      restore: true,
                      page: location.state.page,
                      search: location.state.search,
                      scrollY: location.state.scrollY,
                    },
                  });
                } else {
                  navigate("/inventory");
                }
              }}
            >
              Cancel
            </Button>


            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </Form>
      </Container>
    </DndProvider>
  );
}

export default Editor;